import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import { Box, Button, Divider, IconButton, Menu, MenuItem } from "@mui/material";
import { styled } from "@mui/material/styles";
import type React from "react";
import { useState } from "react";
import { COMPONENTS_TRANSLATION_NAMESPACE } from "src/components/config";
import { NESTED_SELECT_TK } from "src/components/generics/config";
import { SearchInput } from "src/components/generics/SearchInput/SearchInput";
import { useTranslation } from "src/design-system/core/TranslationContext";
import type { Id } from "src/types/id";
import { alphabeticalSort } from "src/utils/alphabeticalSort";
import { ElementIcon } from "./ElementIcon/ElementIcon";
import { menuWidth, useNestedSelectStyles } from "./NestedSelect.styles";

export interface Element {
    label: string;
    value: Id;
    type?: SearchType;
    nested?: Element[];
    disabled?: boolean;
    currentGroupId?: Id;
}

interface NestedSelectProps {
    selected: Element;
    elements: Element[];
    disableGoToGroup?: boolean;
    handleSelect: (element: Element) => void;
}

const NestedSelectButton = styled(Button)(({ theme }) => ({
    width: menuWidth,
    borderRadius: 20,
    border: `1px solid ${theme.palette.border.main}`,
    fontSize: 16,
    fontWeight: "normal",
    transition: theme.transitions.create(["border-color", "box-shadow"]),
    justifyContent: "flex-start",
    paddingRight: theme.spacing(1),
    color: "black",
    fontFamily: [
        "-apple-system",
        "BlinkMacSystemFont",
        '"Segoe UI"',
        "Roboto",
        '"Helvetica Neue"',
        "Arial",
        "sans-serif",
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
    ].join(","),
    "&:focus": {
        borderRadius: 20,
        borderColor: "#80bdff",
        boxShadow: "0 0 0 0.2rem rgba(0,123,255,.25)",
    },
}));

export enum SearchType {
    Organization = "Organization",
    PointOfSales = "PointOfSales",
    PointOfSalesGroup = "PointOfSalesGroup",
    User = "User",
}

export const NestedSelect: React.FC<NestedSelectProps> = ({
    selected,
    elements,
    disableGoToGroup,
    handleSelect,
}): JSX.Element => {
    const classes = useNestedSelectStyles();
    const { t } = useTranslation(`${COMPONENTS_TRANSLATION_NAMESPACE}:${NESTED_SELECT_TK}`);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [current, setCurrent] = useState({
        parent: null,
        elements: null,
        type: null,
    });
    const [query, setQuery] = useState("");
    const allSingleElements = elements
        .reduce((array: Element[], current) => {
            if (current.nested) {
                return [...array, ...current.nested];
            }
            return [...array, current];
        }, [])
        .filter((e) => e.label.toLowerCase().includes(query.toLowerCase()) && e.value !== 0);
    const hasNested = Boolean(elements.find((e) => e.nested));

    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setCurrent({ parent: null, elements: null, type: null });
        setQuery("");
    };

    const handleParentClick = () => {
        handleSelect(current.parent);
        handleClose();
    };

    const handleQueryChange = (newQuery: string) => {
        setQuery(newQuery);
    };

    const handleElementClick = (element: Element) => () => {
        if (element.nested) {
            setCurrent({
                parent: element,
                elements: element.nested,
                type: element.type,
            });
        } else {
            handleSelect(element);
            handleClose();
        }
    };

    const handleGoToParent = () => {
        setCurrent(current.parent);
    };

    const disableAction = (event) => {
        event.stopPropagation();
    };

    let displayedElements = ((query && allSingleElements) || current.elements || elements).filter(
        (e, index, array) => array.map((a) => a.value).indexOf(e.value) === index
    );

    displayedElements = [
        ...displayedElements.filter((e) => e.value <= 0),
        ...alphabeticalSort(
            displayedElements.filter((e) => e.value > 0),
            "label"
        ),
    ];

    return (
        <Box display="flex" alignItems="center">
            <NestedSelectButton
                sx={{ borderRadius: 20 }}
                startIcon={<ElementIcon element={selected} />}
                onClick={handleOpen}
                classes={{
                    startIcon: classes.startIcon,
                }}
            >
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    style={{ width: "100%" }}
                >
                    <span className={classes.selectedText}>{selected.label}</span>
                    <ArrowDropDown className={classes.iconEnd} />
                </Box>
            </NestedSelectButton>
            <Menu
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                onKeyDown={disableAction}
                onKeyDownCapture={disableAction}
                classes={{ paper: classes.menu }}
            >
                {hasNested && (
                    <div>
                        <Box p={1}>
                            <SearchInput placeholder={t("pos")} handleChange={handleQueryChange} />
                        </Box>
                        <Divider />
                    </div>
                )}
                {current.parent && !query && (
                    <MenuItem divider classes={{ root: classes.parent }}>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            className={classes.group}
                        >
                            <Box display="flex" alignItems="center">
                                <IconButton size="small" edge="start" onClick={handleGoToParent}>
                                    <ChevronLeft fontSize="small" />
                                </IconButton>
                                <ElementIcon element={current.parent} />
                                <span className={classes.groupText}>{current.parent.label}</span>
                            </Box>
                            {!disableGoToGroup && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    onClick={handleParentClick}
                                >
                                    {current.type === SearchType.PointOfSalesGroup
                                        ? t("view-group")
                                        : t("view-organization")}
                                </Button>
                            )}
                        </Box>
                    </MenuItem>
                )}
                {displayedElements.map((element, index) => (
                    <MenuItem
                        key={index}
                        onClick={handleElementClick(element)}
                        disabled={element.disabled}
                    >
                        <ElementIcon element={element} />
                        {element.nested ? (
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                style={{ width: "100%" }}
                            >
                                <span className={classes.parentText}>{element.label}</span>
                                <ChevronRight fontSize="small" />
                            </Box>
                        ) : (
                            <span className={classes.text}>{element.label}</span>
                        )}
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
};
